import React from "react";
import { withStyles } from "@material-ui/core/styles";
import FormSectionHeader from "./../common/FormSectionHeader";
import CustomTextField from "../../common/CustomTextField";
import Select from "../../../../../../common/StyledSelect";
import Collapse from "@material-ui/core/Collapse";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import PaymentHeader from "./PaymentHeader";
import PaidCharges from "./PaidCharges";
import {
  convertToUSDollars,
  getPaidCharge,
  isFreightAmountRequired,
  isFreightCheckNumberRequired,
} from "./util";
import {
  PAYMENT_MODES,
  MAX_LENGTH_CONSTRAINTS,
  MIN_VALUE,
} from "../../common/constants";
const { FREIGHT_PAYMENT_MODES, COD_PAYMENT_MODES } = PAYMENT_MODES;
const styles = (theme) => ({
  flexRow: {
    display: "flex",
    direction: "column",
    "&>div": {
      flex: 1,
      width: "100%",
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
  },
  rowContainer: {
    display: "flex",
  },
  columnContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 2.6,
    flexDirection: "column",
  },
  paymentLabel: {
    color: theme.dispatchPanelLabelValueColor,
    fontSize: theme.headerTextFontSize,
    fontWeight: 500,
    lineHeight: "19px",
    display: "flex",
    width: "28%",
    marginTop: "11px",
    flex: 1,
  },
  companyInfo: {
    color: theme.dispatchPanelLabelValueColor,
    fontSize: theme.headerTextFontSize,
    fontWeight: 500,
    lineHeight: "19px",
    letterSpacing: "0.25px",
    marginLeft: "9px",
  },
  warningInfo: {
    color: theme.warningTextColor,
    fontSize: theme.textFontSize,
    fontWeight: 500,
    lineHeight: "19px",
    letterSpacing: "0.25px",
    marginLeft: "9px",
  },
  warningInfoOutlined: {
    display: "flex",
  },
  infoOutlined: {
    display: "flex",
    marginTop: 20,
    marginBottom: 26,
  },
  infoIcon: {
    marginTop: 3,
    color: theme.dispatchPanelLabelValueColor,
  },
  warningInfoIcon: {
    marginTop: 3,
    color: theme.warningTextColor,
  },
  wrapperInner: {
    display: "flex",
    flexDirection: "column",
  },
});

const PaymentDetails = ({ classes, data, errorInfo, handleChange }) => {
  const {
    freightAmount,
    originalFreightAmount,
    freightPaymentType,
    freightCheckNumber,
    freightAmountCollected,
    codAmount,
    originalCodAmount,
    codPaymentType,
    codCheckNumber,
    codAmountCollected,
  } = data;
  const pendingFreightCharge = Number(freightAmount);
  const pendingCodCharge = Number(codAmount);
  const paidFreightCharge = getPaidCharge(
    Number(originalFreightAmount),
    Number(freightAmount)
  );
  const paidCodCharge = getPaidCharge(
    Number(originalCodAmount),
    Number(codAmount)
  );
  const showPaidCharges = paidFreightCharge > 0 || paidCodCharge > 0;
  return (
    <div className={classes.container}>
      <FormSectionHeader label="PAYMENT DETAILS" />
      {showPaidCharges && (
        <PaidCharges
          data={{
            originalFreightAmount,
            originalCodAmount,
            paidCodCharge,
            paidFreightCharge,
          }}
        />
      )}
      <PaymentHeader label="PENDING CHARGES" />
      {pendingFreightCharge > 0 && (
        <div className={classes.rowContainer}>
          <div className={classes.paymentLabel}>
            Freight Charge <br />
            {convertToUSDollars(pendingFreightCharge)}
          </div>
          <div className={classes.columnContainer}>
            <div className={classes.flexRow}>
              <Select
                textFieldProps={{
                  id: "freightPaymentType",
                  label: "Payment Type",
                  variant: "outlined",
                  helperText: errorInfo.freightPaymentType,
                }}
                value={freightPaymentType?.value}
                error={Boolean(errorInfo.freightPaymentType)}
                onChange={handleChange("freightPaymentType")}
                data={FREIGHT_PAYMENT_MODES.FREIGHT_PAYMENT_MODELIST.map(
                  (mode) => ({ label: mode.description, value: mode.code })
                )}
              />
            </div>
            <Collapse
              in={Boolean(freightPaymentType?.value)}
              timeout="auto"
              unmountOnExit
              classes={{ wrapperInner: classes.wrapperInner }}
            >
              {isFreightCheckNumberRequired(freightPaymentType?.value) && (
                <CustomTextField
                  id="freightCheckNumber"
                  label="Cheque#"
                  handleChange={handleChange("freightCheckNumber")}
                  error={errorInfo.freightCheckNumber}
                  value={freightCheckNumber}
                  className={classes.fullWidth}
                  maxLength={MAX_LENGTH_CONSTRAINTS.PAYMENT_TYPE.CHECK}
                />
              )}
              {isFreightAmountRequired(freightPaymentType?.value) && (
                <CustomTextField
                  id="freightAmountCollected"
                  label="Amount Collected"
                  handleChange={handleChange("freightAmountCollected")}
                  error={errorInfo.freightAmountCollected}
                  value={freightAmountCollected}
                  type="number"
                  inputProps={{ min: MIN_VALUE.DEFAULT }}
                  isDecimalAllowed
                  className={classes.fullWidth}
                />
              )}
              {isFreightAmountRequired(freightPaymentType?.value) &&
                pendingFreightCharge !== Number(freightAmountCollected) && (
                  <span className={classes.warningInfoOutlined}>
                    <InfoOutlined className={classes.warningInfoIcon} />
                    <span className={classes.warningInfo}>
                      Amount entered is different than amount to be paid!
                    </span>
                  </span>
                )}
            </Collapse>
          </div>
        </div>
      )}

      {pendingFreightCharge > 0 &&
        FREIGHT_PAYMENT_MODES.COMPANY_ACCOUNT === freightPaymentType?.value && (
          <span className={classes.infoOutlined}>
            <InfoOutlined className={classes.infoIcon} />
            <span className={classes.companyInfo}>
              Company account will be charged later for the above fee.
            </span>
          </span>
        )}

      {pendingCodCharge > 0 && (
        <>
          <FormSectionHeader label="PAYMENT TYPE" />
          <div className={classes.rowContainer}>
            <div className={classes.paymentLabel}>
              COD Amount <br />
              {convertToUSDollars(pendingCodCharge)}
            </div>
            <div className={classes.columnContainer}>
              <div className={classes.flexRow}>
                <Select
                  textFieldProps={{
                    id: "codPaymentType",
                    label: "Payment Type",
                    variant: "outlined",
                    helperText: errorInfo.codPaymentType,
                  }}
                  value={codPaymentType?.value}
                  error={Boolean(errorInfo.codPaymentType)}
                  onChange={handleChange("codPaymentType")}
                  data={COD_PAYMENT_MODES.COD_PAYMENT_MODELIST.map((mode) => ({
                    label: mode.description,
                    value: mode.code,
                  }))}
                />
              </div>
              <Collapse
                in={Boolean(codPaymentType)}
                timeout="auto"
                unmountOnExit
                classes={{ wrapperInner: classes.wrapperInner }}
              >
                <CustomTextField
                  id="chequeNo"
                  label="Cheque#"
                  handleChange={handleChange("codCheckNumber")}
                  error={errorInfo.codCheckNumber}
                  value={codCheckNumber}
                  inputProps={{
                    maxLength: MAX_LENGTH_CONSTRAINTS.PAYMENT_TYPE.CHECK,
                  }}
                  className={classes.fullWidth}
                />
                <CustomTextField
                  id="codAmountCollected"
                  label="Amount Collected"
                  handleChange={handleChange("codAmountCollected")}
                  error={errorInfo.codAmountCollected}
                  value={codAmountCollected}
                  type="number"
                  isDecimalAllowed
                  inputProps={{ min: MIN_VALUE.DEFAULT }}
                  className={classes.fullWidth}
                />
                {pendingCodCharge !== Number(codAmountCollected) && (
                  <span className={classes.warningInfoOutlined}>
                    <InfoOutlined className={classes.warningInfoIcon} />
                    <span className={classes.warningInfo}>
                      Amount entered is different than amount to be paid!
                    </span>
                  </span>
                )}
              </Collapse>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default withStyles(styles)(PaymentDetails);
